import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class TrackingFilterRequest extends BaseObj {
    public EventId: number;
    public __eventid: number;

    public GpsAndEstimated: boolean;
    public __gpsandestimated: boolean;

    public HideFinishedContestants: boolean = false;
    public __hidefinishedcontestants: boolean = false;

    public LabelFontSizeFactor: number = 1;
    public __labelfontsizefactor: number = 1;

    public RaceContestantIds: number[];
    public __racecontestantids: number[];

    public RaceSelection: { [key: string]: boolean };
    public __raceselection: { [key: string]: boolean };

    public ShortLabel: boolean;
    public __shortlabel: boolean;

    public ShowAll: boolean;
    public __showall: boolean;

    public ShowLeading: boolean;
    public __showleading: boolean;

    public ShowOnlyActiveDevices: boolean;
    public __showonlyactivedevices: boolean;

    public ShowOnlyWithDevice: boolean;
    public __showonlywithdevice: boolean;

    public ShowTrailing: boolean;
    public __showtrailing: boolean;

    public TrackIds: number[];
    public __trackids: number[];

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        EntityState?: EntityState;
        EventId?: number;
        GpsAndEstimated?: boolean;
        HideFinishedContestants?: boolean;
        LabelFontSizeFactor?: number;
        RaceContestantIds?: number[];
        RaceSelection?: { [key: string]: boolean };
        ShortLabel?: boolean;
        ShowAll?: boolean;
        ShowLeading?: boolean;
        ShowOnlyActiveDevices?: boolean;
        ShowOnlyWithDevice?: boolean;
        ShowTrailing?: boolean;
        TrackIds?: number[];
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<TrackingFilterRequest>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('TrackingFilterRequest tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.EventId = values.EventId ?? 0;
        this.__eventid = values.EventId ?? 0;
        Object.defineProperty(this, 'EventId', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventId', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GpsAndEstimated = values.GpsAndEstimated ?? false;
        this.__gpsandestimated = values.GpsAndEstimated ?? false;
        Object.defineProperty(this, 'GpsAndEstimated', {
            get(): boolean {
                return this.__gpsandestimated;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('GpsAndEstimated', this.__gpsandestimated, value);
                this.__gpsandestimated = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.HideFinishedContestants = values.HideFinishedContestants ?? false;
        this.__hidefinishedcontestants = values.HideFinishedContestants ?? false;
        Object.defineProperty(this, 'HideFinishedContestants', {
            get(): boolean {
                return this.__hidefinishedcontestants;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('HideFinishedContestants', this.__hidefinishedcontestants, value);
                this.__hidefinishedcontestants = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LabelFontSizeFactor = values.LabelFontSizeFactor ?? 1;
        this.__labelfontsizefactor = values.LabelFontSizeFactor ?? 1;
        Object.defineProperty(this, 'LabelFontSizeFactor', {
            get(): number {
                return this.__labelfontsizefactor;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('LabelFontSizeFactor', this.__labelfontsizefactor, value);
                this.__labelfontsizefactor = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceContestantIds = values.RaceContestantIds ?? [];
        this.__racecontestantids = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.RaceContestantIds ?? [],
            callback: (oldVal, newVal) => this.__entityStateHandler.propertyChanged('RaceContestantIds', oldVal, newVal),
        });
        Object.defineProperty(this, 'RaceContestantIds', {
            get(): number[] {
                return this.__racecontestantids;
            },
            set(value: number[]) {
                this.__entityStateHandler.propertyChanged('RaceContestantIds', this.__racecontestantids, value);
                this.__racecontestantids = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (oldVal: number[], newVal: number[]) =>
                        this.__entityStateHandler.propertyChanged('RaceContestantIds', oldVal, newVal),
                });
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceSelection = values.RaceSelection ?? {};
        this.__raceselection = values.RaceSelection ?? {};
        Object.defineProperty(this, 'RaceSelection', {
            get(): { [key: string]: boolean } {
                return this.__raceselection;
            },
            set(value: { [key: string]: boolean }) {
                this.__entityStateHandler.propertyChanged('RaceSelection', this.__raceselection, value);
                this.__raceselection = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShortLabel = values.ShortLabel ?? false;
        this.__shortlabel = values.ShortLabel ?? false;
        Object.defineProperty(this, 'ShortLabel', {
            get(): boolean {
                return this.__shortlabel;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ShortLabel', this.__shortlabel, value);
                this.__shortlabel = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShowAll = values.ShowAll ?? false;
        this.__showall = values.ShowAll ?? false;
        Object.defineProperty(this, 'ShowAll', {
            get(): boolean {
                return this.__showall;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ShowAll', this.__showall, value);
                this.__showall = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShowLeading = values.ShowLeading ?? false;
        this.__showleading = values.ShowLeading ?? false;
        Object.defineProperty(this, 'ShowLeading', {
            get(): boolean {
                return this.__showleading;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ShowLeading', this.__showleading, value);
                this.__showleading = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShowOnlyActiveDevices = values.ShowOnlyActiveDevices ?? false;
        this.__showonlyactivedevices = values.ShowOnlyActiveDevices ?? false;
        Object.defineProperty(this, 'ShowOnlyActiveDevices', {
            get(): boolean {
                return this.__showonlyactivedevices;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ShowOnlyActiveDevices', this.__showonlyactivedevices, value);
                this.__showonlyactivedevices = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShowOnlyWithDevice = values.ShowOnlyWithDevice ?? false;
        this.__showonlywithdevice = values.ShowOnlyWithDevice ?? false;
        Object.defineProperty(this, 'ShowOnlyWithDevice', {
            get(): boolean {
                return this.__showonlywithdevice;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ShowOnlyWithDevice', this.__showonlywithdevice, value);
                this.__showonlywithdevice = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShowTrailing = values.ShowTrailing ?? false;
        this.__showtrailing = values.ShowTrailing ?? false;
        Object.defineProperty(this, 'ShowTrailing', {
            get(): boolean {
                return this.__showtrailing;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ShowTrailing', this.__showtrailing, value);
                this.__showtrailing = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TrackIds = values.TrackIds ?? [];
        this.__trackids = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.TrackIds ?? [],
            callback: (oldVal, newVal) => this.__entityStateHandler.propertyChanged('TrackIds', oldVal, newVal),
        });
        Object.defineProperty(this, 'TrackIds', {
            get(): number[] {
                return this.__trackids;
            },
            set(value: number[]) {
                this.__entityStateHandler.propertyChanged('TrackIds', this.__trackids, value);
                this.__trackids = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (oldVal: number[], newVal: number[]) => this.__entityStateHandler.propertyChanged('TrackIds', oldVal, newVal),
                });
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventId": ${JSON.stringify(this.EventId)}` +
            ',' +
            `"GpsAndEstimated": ${JSON.stringify(this.GpsAndEstimated)}` +
            ',' +
            `"HideFinishedContestants": ${JSON.stringify(this.HideFinishedContestants)}` +
            ',' +
            `"LabelFontSizeFactor": ${JSON.stringify(this.LabelFontSizeFactor)}` +
            ',' +
            `"RaceContestantIds": [ ${this.RaceContestantIds.map((x) => JSON.stringify(x))} ]` +
            ',' +
            `"RaceSelection": ${JSON.stringify(this.RaceSelection)}` +
            ',' +
            `"ShortLabel": ${JSON.stringify(this.ShortLabel)}` +
            ',' +
            `"ShowAll": ${JSON.stringify(this.ShowAll)}` +
            ',' +
            `"ShowLeading": ${JSON.stringify(this.ShowLeading)}` +
            ',' +
            `"ShowOnlyActiveDevices": ${JSON.stringify(this.ShowOnlyActiveDevices)}` +
            ',' +
            `"ShowOnlyWithDevice": ${JSON.stringify(this.ShowOnlyWithDevice)}` +
            ',' +
            `"ShowTrailing": ${JSON.stringify(this.ShowTrailing)}` +
            ',' +
            `"TrackIds": [ ${this.TrackIds.map((x) => JSON.stringify(x))} ]` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
                     * Sets the entity state to Added
                     *
                     
                     * 
                     * Note: This does not do anything with the children, so use with caution
                     * 
                     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
                     */
    public SetEntityStateAsAddedAndResetId() {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';

        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of TrackingFilterRequest from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        EventId?: number;
        GpsAndEstimated?: boolean;
        HideFinishedContestants?: boolean;
        LabelFontSizeFactor?: number;
        RaceContestantIds?: number[];
        RaceSelection?: { [key: string]: boolean };
        ShortLabel?: boolean;
        ShowAll?: boolean;
        ShowLeading?: boolean;
        ShowOnlyActiveDevices?: boolean;
        ShowOnlyWithDevice?: boolean;
        ShowTrailing?: boolean;
        TrackIds?: number[];
        EntityState?: 'Added' | 'Unchanged';
    }): TrackingFilterRequest {
        return new TrackingFilterRequest(values);
    }

    public static _FromServerJson(json: any): TrackingFilterRequest {
        return new TrackingFilterRequest({
            EventId: json.EventId,
            GpsAndEstimated: json.GpsAndEstimated,
            HideFinishedContestants: json.HideFinishedContestants,
            LabelFontSizeFactor: json.LabelFontSizeFactor,
            RaceContestantIds: json.RaceContestantIds,
            RaceSelection: json.RaceSelection,
            ShortLabel: json.ShortLabel,
            ShowAll: json.ShowAll,
            ShowLeading: json.ShowLeading,
            ShowOnlyActiveDevices: json.ShowOnlyActiveDevices,
            ShowOnlyWithDevice: json.ShowOnlyWithDevice,
            ShowTrailing: json.ShowTrailing,
            TrackIds: json.TrackIds,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of TrackingFilterRequest from another instance of TrackingFilterRequest.
     * @param other The TrackingFilterRequest you want to clone
     * @returns A cloned TrackingFilterRequest
     */
    public static FromOther(other: TrackingFilterRequest): TrackingFilterRequest {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return TrackingFilterRequest._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return TrackingFilterRequest.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'EventId'
            | 'GpsAndEstimated'
            | 'HideFinishedContestants'
            | 'LabelFontSizeFactor'
            | 'RaceContestantIds'
            | 'RaceSelection'
            | 'ShortLabel'
            | 'ShowAll'
            | 'ShowLeading'
            | 'ShowOnlyActiveDevices'
            | 'ShowOnlyWithDevice'
            | 'ShowTrailing'
            | 'TrackIds'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'TrackingFilterRequest';
}
